import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cx } from './cx';

export interface TabItem {
  id: string;
  label: string;
  panel: ReactNode;
}

/**
 * govuk/components/tabs in its enhanced state (what tabs.js produces): tablist/tab/tabpanel roles,
 * roving tabindex and arrow-key navigation. Implemented in React because the original script rewrites
 * window.location.hash (which would fight the router) and toggles panels outside React's control.
 */
export function Tabs({ title, items, selectedId, onSelect }: { title: string; items: TabItem[]; selectedId?: string; onSelect?: (id: string) => void }) {
  const [internal, setInternal] = useState(items[0]?.id);
  const selected = items.some((i) => i.id === (selectedId ?? internal)) ? (selectedId ?? internal) : items[0]?.id;
  const tabRefs = useRef<Record<string, HTMLAnchorElement | null>>({});
  const select = (id: string, focus = false) => {
    setInternal(id);
    onSelect?.(id);
    if (focus) tabRefs.current[id]?.focus();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLAnchorElement>, index: number) => {
    const last = items.length - 1;
    let next: number | null = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = index === last ? 0 : index + 1;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = index === 0 ? last : index - 1;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = last;
    if (next !== null) {
      event.preventDefault();
      select(items[next]!.id, true);
    }
  };
  return (
    <div className="govuk-tabs" data-module="govuk-tabs">
      <h2 className="govuk-tabs__title">{title}</h2>
      {/* Same roles as govuk tabs.js sets on the list after enhancement. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role */}
      <ul className="govuk-tabs__list" role="tablist">
        {items.map((item, index) => (
          <li key={item.id} className={cx('govuk-tabs__list-item', item.id === selected && 'govuk-tabs__list-item--selected')} role="presentation">
            <a
              ref={(el) => {
                tabRefs.current[item.id] = el;
              }}
              className="govuk-tabs__tab"
              href={`#${item.id}`}
              id={`tab_${item.id}`}
              role="tab"
              aria-controls={item.id}
              aria-selected={item.id === selected}
              tabIndex={item.id === selected ? 0 : -1}
              onClick={(e) => {
                e.preventDefault();
                select(item.id);
              }}
              onKeyDown={(e) => onKeyDown(e, index)}
            >
              {item.label}
            </a>
          </li>
        ))}
      </ul>
      {items.map((item) => (
        <div
          key={item.id}
          className={cx('govuk-tabs__panel', item.id !== selected && 'govuk-tabs__panel--hidden')}
          id={item.id}
          role="tabpanel"
          aria-labelledby={`tab_${item.id}`}
          tabIndex={-1}
          hidden={item.id !== selected}
        >
          {item.id === selected && item.panel}
        </div>
      ))}
    </div>
  );
}
